/**
 * ComposeMessageModal Component
 *
 * Flexible modal for composing and sending messages.
 * Works from three contexts:
 * 1. Messages page - autocomplete user search
 * 2. Event detail - pre-selected event (all crew on that event)
 * 3. Crew detail - pre-selected single user
 */

import { useState, useEffect, FormEvent } from 'react';
import toast from 'react-hot-toast';
import { sendMessage, type MessageType } from '../../api/messages';
import { getEvent, type Event } from '../../api/events';
import { notifyAllPending } from '../../api/assignments';
import { listUsers } from '../../api/users';
import { getErrorMessage } from '../../api/client';
import { getSmsUsage, smsTextsRemaining, type SmsUsage } from '../../api/subscription';
import { useOrganization } from '../../context/OrganizationContext';
import { isPendingNotification } from '../../utils/pendingNotification';
import RecipientSearch from './RecipientSearch';
import Modal from '../ui/Modal';

interface SelectedUser {
  id: string;
  name: string;
}

interface ComposeMessageModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  // Pre-selection modes (mutually exclusive)
  preselectedUsers?: SelectedUser[];  // From crew detail (single user)
  preselectedEventId?: string;        // From event detail (all event crew)
  // If neither prop is provided: Shows autocomplete + "Send to entire org" toggle
  // Event mode: called after assignment notifications are queued from the
  // in-modal notice, so the parent page can update its pending-state display.
  onPendingNotificationsQueued?: () => void;
}

const SMS_MAX_LENGTH = 140;

const MESSAGE_TYPES: { value: MessageType; label: string }[] = [
  { value: 'BROADCAST', label: 'Direct Message' },
];

export default function ComposeMessageModal({
  isOpen,
  onClose,
  onSuccess,
  preselectedUsers,
  preselectedEventId,
  onPendingNotificationsQueued,
}: ComposeMessageModalProps) {
  const { smsEnabled } = useOrganization();

  // Form state
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [messageType, setMessageType] = useState<MessageType>('BROADCAST');
  const [sendEmail, setSendEmail] = useState(true);
  // Event mode: attach the event's details block to the delivered email.
  const [includeEventInfo, setIncludeEventInfo] = useState(true);
  const [sendSms, setSendSms] = useState(false);
  const [sendToEntireOrg, setSendToEntireOrg] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<SelectedUser[]>([]);

  // Event loading state (when preselectedEventId is provided)
  const [event, setEvent] = useState<Event | null>(null);
  const [loadingEvent, setLoadingEvent] = useState(false);
  const [eventError, setEventError] = useState<string | null>(null);

  // Event mode: assignment-notification state. A broadcast from this modal is
  // NOT the assignment notification (role, call time, confirm/decline link) —
  // the notice below surfaces crew who never got theirs, with a send action.
  const [sendingNotifications, setSendingNotifications] = useState(false);
  const [notificationsQueued, setNotificationsQueued] = useState(false);

  // Active org-member count for the "Send to entire organization" preview.
  // Backend sendToAllCrew targets every active org member (any role).
  const [activeMemberCount, setActiveMemberCount] = useState<number | null>(null);

  // Texting allowance, shown next to the SMS checkbox (best-effort read).
  const [smsUsage, setSmsUsage] = useState<SmsUsage | null>(null);

  // Submission state
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine the mode
  const isEventMode = !!preselectedEventId;
  const isUserMode = !!preselectedUsers && preselectedUsers.length > 0;
  const isSearchMode = !isEventMode && !isUserMode;

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSubject('');
      setBody('');
      setMessageType('BROADCAST');
      setSendEmail(true);
      setSendSms(false);
      setSendToEntireOrg(false);
      setSelectedUsers(preselectedUsers || []);
      setError(null);
      setEvent(null);
      setEventError(null);
      setNotificationsQueued(false);

      // Load event if needed
      if (preselectedEventId) {
        loadEvent(preselectedEventId);
      }

      setSmsUsage(null);
      if (smsEnabled) {
        getSmsUsage()
          .then(setSmsUsage)
          .catch(() => setSmsUsage(null));
      }

      // Preload the active-member count so the "send to entire org" toggle
      // shows a real number instead of just "everyone". Counts the ACTIVE
      // roster specifically, which is the same audience a broadcast reaches —
      // archived members are skipped by both.
      if (!preselectedEventId && (!preselectedUsers || preselectedUsers.length === 0)) {
        setActiveMemberCount(null);
        listUsers({ state: 'ACTIVE', limit: 1 })
          .then((res) => setActiveMemberCount(res.pagination.total))
          .catch(() => setActiveMemberCount(null));
      }
    }
  }, [isOpen, preselectedUsers, preselectedEventId, smsEnabled]);

  // Disable SMS when body exceeds limit
  useEffect(() => {
    if (body.length > SMS_MAX_LENGTH && sendSms) {
      setSendSms(false);
    }
  }, [body, sendSms]);

  const loadEvent = async (eventId: string) => {
    setLoadingEvent(true);
    setEventError(null);
    try {
      const { event } = await getEvent(eventId);
      setEvent(event);
    } catch (err) {
      setEventError(getErrorMessage(err));
    } finally {
      setLoadingEvent(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);

    // Validation
    if (!subject.trim()) {
      setError('Subject is required');
      return;
    }
    if (!body.trim()) {
      setError('Message body is required');
      return;
    }
    if (!sendEmail && !sendSms) {
      setError('Select at least one channel (Email or SMS)');
      return;
    }

    // Validate recipients
    if (isSearchMode && !sendToEntireOrg && selectedUsers.length === 0) {
      setError('Select at least one recipient or send to entire organization');
      return;
    }

    setSubmitting(true);
    try {
      await sendMessage({
        subject: subject.trim(),
        body: body.trim(),
        targetEventId: isEventMode ? preselectedEventId : undefined,
        includeEventInfo: isEventMode ? includeEventInfo : undefined,
        recipientUserIds: isUserMode
          ? preselectedUsers!.map(u => u.id)
          : isSearchMode && !sendToEntireOrg
            ? selectedUsers.map(u => u.id)
            : undefined,
        sendToAllCrew: isSearchMode && sendToEntireOrg ? true : undefined,
        sendViaEmail: sendEmail,
        sendViaSms: sendSms,
      });

      onSuccess();
      onClose();
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSubmitting(false);
    }
  };

  const canEnableSms = body.length <= SMS_MAX_LENGTH;
  // Org gate (kill switch + Enterprise tier), computed server-side.
  const orgSmsOff = !smsEnabled;
  const hasAssignedCrew = event?.roles?.some(r => (r.assignments?.length || 0) > 0);

  // Crew on this event who never received their assignment notification and
  // are still waiting to respond (utils/pendingNotification.ts — the backend's
  // rule; a confirmed person is not pending). One assignment per user per
  // event, so this is a user count.
  const pendingNotificationCount =
    isEventMode && event && !notificationsQueued
      ? (event.roles ?? [])
          .flatMap(r => r.assignments ?? [])
          .filter(a =>
            isPendingNotification(a as { status: string; notificationSentAt?: string | null }, event)
          ).length
      : 0;

  const handleSendPendingNotifications = async () => {
    if (!preselectedEventId) return;
    setSendingNotifications(true);
    try {
      const result = await notifyAllPending(preselectedEventId);
      setNotificationsQueued(true);
      onPendingNotificationsQueued?.();
      toast.success(
        `Assignment notifications on their way to ${result.usersQueued} crew member${
          result.usersQueued !== 1 ? 's' : ''
        }.`
      );
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSendingNotifications(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
      labelledBy="compose-message-title"
      contentClassName="max-h-[90vh] flex flex-col"
    >
      <form onSubmit={handleSubmit} className="flex flex-col h-full">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 id="compose-message-title" className="text-lg font-semibold text-gray-900">
              Compose Message
            </h2>
            <button
              type="button"
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
              aria-label="Close"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                {error}
              </div>
            )}

            {/* Recipients section */}
            <div>
              {isEventMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipients
                  </label>
                  {loadingEvent ? (
                    <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-sm text-gray-500">
                      Loading event...
                    </div>
                  ) : eventError ? (
                    <div className="px-3 py-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-600">
                      {eventError}
                    </div>
                  ) : event ? (
                    <div className="px-3 py-2 bg-surface-panel border border-hairline border-l-[3px] border-l-rack text-sm text-primary-800">
                      All crew assigned to <strong>{event.name}</strong>
                      {!hasAssignedCrew && (
                        <span className="block text-xs text-amber-600 mt-1">
                          Note: No crew currently assigned to this event
                        </span>
                      )}
                    </div>
                  ) : null}

                  {/* Event context toggle — checked by default so the email
                      carries which event the message is about. */}
                  <label className="mt-2 flex items-start gap-2 text-sm text-gray-700">
                    <input
                      type="checkbox"
                      checked={includeEventInfo}
                      onChange={(e) => setIncludeEventInfo(e.target.checked)}
                      className="mt-0.5 h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-rack"
                    />
                    <span>
                      Include this event's information in the message
                      <span className="block text-xs text-gray-500">
                        Adds the event name, date, venue, and call time to the email.
                      </span>
                    </span>
                  </label>

                  {/* Un-notified crew notice: a broadcast is not the assignment
                      notification — offer to send those right here. */}
                  {pendingNotificationCount > 0 && (
                    <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-md">
                      <p className="text-sm text-amber-900">
                        {pendingNotificationCount} of these crew member
                        {pendingNotificationCount !== 1 ? 's' : ''}{' '}
                        {pendingNotificationCount !== 1 ? "haven't" : "hasn't"} received their
                        assignment notification yet — this message is separate and won't include
                        their assignment details.
                      </p>
                      <button
                        type="button"
                        onClick={handleSendPendingNotifications}
                        disabled={sendingNotifications}
                        className="mt-2 px-3 py-1.5 text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 rounded-md disabled:opacity-50"
                      >
                        {sendingNotifications
                          ? 'Sending…'
                          : `Send assignment notification${
                              pendingNotificationCount !== 1 ? 's' : ''
                            } (${pendingNotificationCount})`}
                      </button>
                    </div>
                  )}
                </div>
              )}

              {isUserMode && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient
                  </label>
                  <div className="flex flex-wrap gap-1.5">
                    {preselectedUsers!.map(user => (
                      <span
                        key={user.id}
                        className="inline-flex items-center px-2 py-1 bg-primary-100 text-primary-800 text-sm"
                      >
                        {user.name}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {isSearchMode && (
                <div className="space-y-3">
                  {/* Send to entire org toggle */}
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendToEntireOrg}
                      onChange={(e) => setSendToEntireOrg(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-rack"
                    />
                    <span className="text-sm text-gray-700">
                      Send to entire organization
                      {activeMemberCount !== null && (
                        <span className="ml-1 text-gray-500">
                          ({activeMemberCount} active member{activeMemberCount !== 1 ? 's' : ''})
                        </span>
                      )}
                    </span>
                  </label>

                  {/* Recipient search (hidden when sending to entire org) */}
                  {!sendToEntireOrg && (
                    <RecipientSearch
                      selectedUsers={selectedUsers}
                      onSelectionChange={setSelectedUsers}
                    />
                  )}
                </div>
              )}
            </div>

            {/* Message Type */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Type
              </label>
              <select
                value={messageType}
                onChange={(e) => setMessageType(e.target.value as MessageType)}
                className="w-full pl-3 pr-10 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack"
              >
                {MESSAGE_TYPES.map(type => (
                  <option key={type.value} value={type.value}>
                    {type.label}
                  </option>
                ))}
              </select>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Subject <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="Enter subject..."
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack"
              />
            </div>

            {/* Body */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Message <span className="text-red-500">*</span>
              </label>
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Type your message..."
                rows={5}
                className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:ring-rack focus:border-rack resize-none"
              />
              {sendSms && (
                <p className={`mt-1 text-xs ${body.length > SMS_MAX_LENGTH ? 'text-red-500' : 'text-gray-500'}`}>
                  {body.length} / {SMS_MAX_LENGTH} characters
                </p>
              )}
            </div>

            {/* Channels */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Send via
              </label>
              <div className="flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={sendEmail}
                    onChange={(e) => setSendEmail(e.target.checked)}
                    className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-rack"
                  />
                  <span className="text-sm text-gray-700">Email</span>
                </label>

                {orgSmsOff ? (
                  <span className="text-xs text-gray-400">
                    Text messaging isn't enabled for this organization
                  </span>
                ) : canEnableSms ? (
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={sendSms}
                      onChange={(e) => setSendSms(e.target.checked)}
                      className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-rack"
                    />
                    <span className="text-sm text-gray-700">SMS</span>
                  </label>
                ) : (
                  <span className="text-xs text-gray-400">
                    SMS unavailable for messages over {SMS_MAX_LENGTH} characters
                  </span>
                )}
              </div>
              {sendSms && smsUsage && <SmsAllowanceHint usage={smsUsage} />}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <div className="flex justify-end gap-3">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-100 rounded-md transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 rounded-md transition-colors disabled:opacity-50"
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </div>
          </div>
      </form>
    </Modal>
  );
}

/**
 * One line under the SMS checkbox: how many texts are left this month, and a
 * plain warning when texting is paused or nearly out. Texts only reach crew
 * who opted in; everyone else gets the email either way.
 */
export function SmsAllowanceHint({ usage }: { usage: SmsUsage }) {
  const remaining = smsTextsRemaining(usage);
  if (remaining === null) {
    return <p className="mt-2 text-xs text-gray-500">Texts go to crew who opted in. Unlimited texts for this organization.</p>;
  }
  const resets = new Date(usage.resetsAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  if (remaining === 0) {
    return (
      <p className="mt-2 text-xs text-red-600" data-testid="sms-paused-hint">
        Texting is paused until {resets}: this message will go by email only. Owners can add a text pack in Settings &gt; Billing.
      </p>
    );
  }
  const low = remaining <= 25;
  return (
    <p className={`mt-2 text-xs ${low ? 'text-amber-700' : 'text-gray-500'}`}>
      Texts go to crew who opted in, one text per person. {remaining.toLocaleString('en-US')} text
      {remaining === 1 ? '' : 's'} left this month
      {low ? '; anyone past that gets the email only.' : '.'}
    </p>
  );
}

